"use client";

import React, { ReactNode, createContext, useContext, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useMessageAction } from "@/lib/hooks/api/messages";
import { MessagesContextType } from "@/lib/core/crm/types";
import { createInitialState } from "./state";
import { useGetters } from "./getters";
import { useSetters } from "./setters";
import { useActions } from "./actions";
import { useUtils } from "./utils";

const MessagesContext = createContext<MessagesContextType | undefined>(undefined);

export function MessagesProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const messageActionMutation = useMessageAction();

  // Initialize state
  const [state, setState] = React.useState(createInitialState());

  // Call hooks at top level (not inside useMemo)
  const getters = useGetters(state);
  const setters = useSetters(setState);
  const actions = useActions(state, setState, setters.updateMessage, messageActionMutation);
  const utils = useUtils(state, queryClient);

  // Memoize context value
  const contextValue = useMemo<MessagesContextType>(
    () => ({
      state,
      ...getters,
      ...setters,
      ...actions,
      ...utils,
    }),
    [state, getters, setters, actions, utils]
  );

  return <MessagesContext.Provider value={contextValue}>{children}</MessagesContext.Provider>;
}

// Custom hook to use the messages context
export function useMessagesContext(): MessagesContextType {
  const context = useContext(MessagesContext);
  if (context === undefined) {
    throw new Error("useMessagesContext must be used within a MessagesProvider");
  }
  return context;
}
